import { i18n, serverText, useServerText } from "@/i18n";
import { useTranslation } from "react-i18next";
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import { forwardRef } from "react";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Small primitives, hand-written against the theme tokens.
 *
 * Deliberately not a component library: there are six of these, they are read
 * far more often than they are changed, and a token-driven button is easier to
 * reason about than an override chain.
 */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "icon";
};

export function Button({
  className,
  variant = "secondary",
  size = "md",
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium",
        // A pill has a fixed height, so a label that wraps spills outside its
        // own background. Labels never wrap; tight rows wrap whole buttons.
        "whitespace-nowrap",
        "transition-colors duration-150 select-none",
        "disabled:pointer-events-none disabled:opacity-50",
        {
          primary: "bg-accent text-accent-fg hover:bg-accent-hover",
          secondary:
            "bg-surface text-text border border-border hover:bg-surface-hover hover:border-border-strong",
          ghost: "text-text-muted hover:bg-surface-hover hover:text-text",
          danger: "bg-danger text-danger-fg hover:opacity-90",
        }[variant],
        {
          sm: "h-8 px-3 text-[13px]",
          md: "h-9.5 px-4 text-sm",
          icon: "h-9 w-9",
        }[size],
        className,
      )}
      {...props}
    />
  );
}

/** Forwards its ref, so a dialog built on it can trap focus inside itself. */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Card({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-xl border border-border bg-surface",
          "shadow-[var(--shadow-sm)]",
          className,
        )}
        {...props}
      />
    );
  },
);

export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: "neutral" | "accent" | "success" | "warning" | "danger";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5",
        "text-[11px] font-medium tracking-wide uppercase",
        {
          neutral: "bg-bg-subtle text-text-muted",
          accent: "bg-accent-subtle text-accent",
          success: "bg-bg-subtle text-success",
          warning: "bg-bg-subtle text-warning",
          danger: "bg-danger-subtle text-danger",
        }[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Skeleton placeholder.
 *
 * Sized to the content it stands in for so the layout does not jump when real
 * data arrives — a shifting page is worse than a blank one.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-bg-subtle", className)}
      aria-hidden="true"
    />
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
      {icon ? <div className="mb-4 text-text-faint">{icon}</div> : null}
      <h2 className="text-base font-semibold text-text">{title}</h2>
      <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-text-muted">
        {description}
      </p>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

/**
 * A failed load, said plainly, with a way out.
 *
 * Every list screen used to branch loading → empty, with no error branch — so a
 * 500 or a dropped connection rendered "No recordings yet" beside an invitation
 * to record one. A total backend failure looked pixel-identical to a brand-new
 * account, which is the same class of dishonesty as the old "processing" badge
 * on a recording that had actually failed.
 */
export function ErrorState({
  title,
  error,
  onRetry,
}: {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
}) {
  const { t } = useTranslation(["shell", "common"]);
  const st = useServerText();
  // A plain string is a sentence the caller wrote for the user — show it.
  // Discarding it (as this once did) meant hand-written advice like "reload
  // the page" silently became the generic line.
  const detail =
    typeof error === "string" && error
      ? error
      : error instanceof Error && error.message
        ? st(error.message)
        : t("errorState.body");

  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-danger-subtle text-danger">
        <AlertTriangle size={17} />
      </div>
      <h2 className="text-base font-semibold text-text">{title ?? t("errorState.title")}</h2>
      <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-text-muted">{detail}</p>
      {onRetry ? (
        <Button variant="secondary" className="mt-5" onClick={onRetry}>
          <RefreshCw size={14} />
          {t("common:actions.tryAgain")}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Inline error for a form or an action, rather than a whole screen.
 *
 * Hand-rolled six times across the app with slightly different markup each
 * time; `role="alert"` was present in some copies and missing in others.
 */
export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] leading-relaxed text-danger"
    >
      {children}
    </p>
  );
}

/**
 * Turn whatever a mutation rejected with into a sentence, in the interface's
 * language. Server sentences go through the shared catalogue; one it does not
 * know comes back as written, never blank.
 */
export function errorMessage(error: unknown, fallback?: string): string {
  return error instanceof Error && error.message
    ? serverText(error.message)
    : (fallback ?? i18n.t("errors.generic"));
}

/**
 * A single-line text input, styled once.
 *
 * The same fourteen-class string was repeated in fourteen places, so a change to
 * focus or border styling meant finding all of them.
 */
export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "h-9 w-full rounded-lg border border-border bg-bg px-3 text-[13px] text-text",
        "placeholder:text-text-faint transition-colors",
        "focus:border-accent focus:outline-none disabled:opacity-60",
        className,
      )}
    />
  );
}

/**
 * Two-step confirmation, inline.
 *
 * The pattern was hand-written six times and forgotten in four places that
 * needed it more: removing someone from a workspace was a single icon click
 * with no undo, and "Make operator" granted platform-wide access to every
 * customer's data in one press. Confirming some destructive actions and not
 * others is worse than either extreme, because it teaches that a click is safe.
 */
export function ConfirmInline({
  question,
  confirmLabel,
  onConfirm,
  onCancel,
  pending,
}: {
  question: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  pending?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-danger/40 bg-danger-subtle/40 p-3">
      <div className="text-[13px] leading-relaxed text-text">{question}</div>
      <div className="mt-2.5 flex gap-2">
        <Button variant="danger" size="sm" disabled={pending} onClick={onConfirm}>
          {pending ? <Loader2 size={13} className="animate-spin" /> : null}
          {confirmLabel}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          {t("actions.cancel")}
        </Button>
      </div>
    </div>
  );
}
